import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Database as DatabaseIcon, Plus, Trash2, Search, X, Mail, Phone, User, Upload, Loader2, Merge, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { NumberInput } from '../components/ui/number-input';
import { TierBadge, TierPicker } from '../components/TierBadge';
import { computeDuplicateIds } from '../lib/duplicateContacts';

const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/uploads/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data.url;
};

const empty = () => ({ name: '', email: '', phone: '', company: '', role: '', handle: '', ig_link: '', tt_link: '', ig_followers: '', tt_followers: '', notes: '', address: '', tags: [], tier: '', custom_tag: '' });

const Database = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [mailerFilter, setMailerFilter] = useState('any');
  const [mailerTypeFilter, setMailerTypeFilter] = useState('any');
  const [editing, setEditing] = useState(null);   // contact object or null
  const [merging, setMerging] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [addTarget, setAddTarget] = useState('');
  const [addingToMailer, setAddingToMailer] = useState(false);
  const [importing, setImporting] = useState(false);

  const importSpreadsheet = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/database/contacts/import-spreadsheet', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await reload();
      toast.success(`imported — ${data.added} added, ${data.merged} merged`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'import failed'); }
    finally { setImporting(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/database/contacts');
        setContacts(data || []);
      } catch { toast.error('failed to load contacts'); }
      finally { setLoading(false); }
    })();
    (async () => {
      try { const { data } = await api.get('/mailers/campaigns'); setCampaigns(data || []); } catch { /* ignore */ }
    })();
  }, []);

  const allTags = useMemo(() => {
    const s = new Set();
    contacts.forEach((c) => (c.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [contacts]);

  const duplicateIds = useMemo(() => computeDuplicateIds(contacts), [contacts]);

  const allMailers = useMemo(() => {
    const m = new Map();
    contacts.forEach((c) => (c.mailer_history || []).forEach((h) => m.set(h.campaign_id, h.campaign_name)));
    return Array.from(m.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [contacts]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    const arr = contacts.filter((c) => {
      if (selectedTags.length && !selectedTags.every((tag) => (c.tags || []).includes(tag))) return false;
      if (mailerFilter !== 'any' && !(c.mailer_history || []).some((h) => h.campaign_id === mailerFilter && h.content_posted)) return false;
      if (mailerTypeFilter !== 'any' && !(c.mailer_history || []).some((h) => h.campaign_type === mailerTypeFilter)) return false;
      if (!t) return true;
      return [c.name, c.email, c.company, c.role, c.handle].filter(Boolean).some((v) => v.toLowerCase().includes(t));
    });
    return [...arr].sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
  }, [contacts, q, selectedTags, mailerFilter, mailerTypeFilter]);

  const save = async (draft) => {
    try {
      if (draft.id) {
        const { data } = await api.patch(`/database/contacts/${draft.id}`, draft);
        setContacts((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      } else {
        const { data } = await api.post('/database/contacts', draft);
        setContacts((prev) => [...prev, data].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      }
      setEditing(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'save failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('remove this contact?')) return;
    try { await api.delete(`/database/contacts/${id}`); setContacts((prev) => prev.filter((c) => c.id !== id)); }
    catch { toast.error('delete failed'); }
  };

  const toggleTag = (tag) => setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

  const reload = async () => {
    try { const { data } = await api.get('/database/contacts'); setContacts(data || []); } catch { /* keep current */ }
  };

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());

  const addSelectedToMailer = async () => {
    if (!addTarget || !selectedIds.size) { toast.error('pick a mailer first'); return; }
    setAddingToMailer(true);
    try {
      const ids = [...selectedIds];
      await api.post(`/mailers/campaigns/${addTarget}/creators/from-contacts`, { contact_ids: ids });
      toast.success(`added ${ids.length} contact${ids.length === 1 ? '' : 's'} to mailer (duplicates skipped)`);
      clearSelection();
      setAddTarget('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'failed to add to mailer'); }
    finally { setAddingToMailer(false); }
  };

  const setTier = async (id, tier) => {
    try {
      const { data } = await api.patch(`/database/contacts/${id}`, { tier });
      setContacts((prev) => prev.map((c) => (c.id === data.id ? data : c)));
    } catch { toast.error('failed to set tier'); }
  };

  const correctDuplicates = async () => {
    const count = duplicateIds.size;
    if (!count) { toast.info('no duplicates to merge'); return; }
    if (!window.confirm(`merge ${count} duplicate contact${count === 1 ? '' : 's'} into single cards? data and mailer history from each will be combined.`)) return;
    setMerging(true);
    try {
      const { data: res } = await api.post('/database/contacts/merge-duplicates');
      const { data } = await api.get('/database/contacts');
      setContacts(data || []);
      toast.success(res.merged_groups ? `merged ${res.removed} duplicate${res.removed === 1 ? '' : 's'} into ${res.merged_groups} card${res.merged_groups === 1 ? '' : 's'}` : 'no duplicates found');
    } catch (e) { toast.error(e?.response?.data?.detail || 'merge failed'); }
    finally { setMerging(false); }
  };

  return (
    <div className="p-6 lg:p-8">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <DatabaseIcon className="w-6 h-6 text-pink-500" /> database
          </h1>
          <p className="text-sm text-slate-500 mt-1">every contact you know — press, editors, brands, friends of the roster. tag them into collections.</p>
        </div>
        {isManager && (
          <div className="flex items-center gap-2">
            {duplicateIds.size > 0 && (
              <Button onClick={correctDuplicates} disabled={merging} variant="outline" className="text-xs h-9 border-red-300 text-red-700 hover:bg-red-50" data-testid="db-correct-duplicates">
                {merging ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Merge className="w-3.5 h-3.5 mr-1" />}
                correct duplicates ({duplicateIds.size})
              </Button>
            )}
            <label className={`inline-flex items-center gap-1 h-9 px-3 rounded-md text-xs font-medium border cursor-pointer transition-colors ${importing ? 'opacity-60 pointer-events-none' : 'bg-white text-slate-600 border-slate-200 hover:border-pink-400 hover:text-pink-700'}`} data-testid="db-import-spreadsheet-btn" title="upload an Excel/CSV — Claude reads the contacts and adds them, merging duplicates">
              {importing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} import spreadsheet
              <input type="file" accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importSpreadsheet(f); }} data-testid="db-import-spreadsheet-input" />
            </label>
            <Button onClick={() => setEditing(empty())} className="bg-pink-500 hover:bg-pink-600 text-white text-xs h-9" data-testid="db-add-contact">
              <Plus className="w-3.5 h-3.5 mr-1" /> new contact
            </Button>
          </div>
        )}
      </header>

      <div className="bg-white border border-slate-200 rounded-2xl p-3 mb-4 flex items-center gap-2 flex-wrap">
        <Search className="w-4 h-4 text-slate-400 shrink-0" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="search by name, email, company, handle…"
          className="h-9 text-xs border-none focus-visible:ring-0 flex-1 min-w-[160px]"
          data-testid="db-search"
        />
        <div className="w-px h-5 bg-slate-200" />
        <Select value={mailerFilter} onValueChange={setMailerFilter}>
          <SelectTrigger className="w-48 h-8 text-xs" data-testid="db-filter-mailer"><SelectValue placeholder="posted which mailer" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">posted mailer: any</SelectItem>
            {allMailers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={mailerTypeFilter} onValueChange={setMailerTypeFilter}>
          <SelectTrigger className="w-40 h-8 text-xs" data-testid="db-filter-mailer-type"><SelectValue placeholder="mailer type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">mailer type: any</SelectItem>
            <SelectItem value="organic">organic mailer</SelectItem>
            <SelectItem value="paid">paid campaign</SelectItem>
            <SelectItem value="affiliate">tts affiliate</SelectItem>
          </SelectContent>
        </Select>
        {(mailerFilter !== 'any' || mailerTypeFilter !== 'any') && (
          <button onClick={() => { setMailerFilter('any'); setMailerTypeFilter('any'); }} className="text-[11px] text-slate-400 hover:text-pink-600 underline" data-testid="db-clear-mailer-filters">clear</button>
        )}
        <div className="text-[11px] text-slate-500 ml-auto">{filtered.length} of {contacts.length}</div>
      </div>

      {allTags.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-1.5 items-center">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mr-1">tags</span>
          {allTags.map((tag) => {
            const on = selectedTags.includes(tag);
            return (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`inline-flex items-center gap-1 h-7 px-3 rounded-full text-[12px] font-semibold border transition ${
                  on ? 'bg-pink-500 text-white border-pink-500 shadow-sm' : 'bg-pink-50 text-pink-700 border-pink-200 hover:border-pink-400'
                }`}
                data-testid={`db-tag-${tag}`}
              >
                {tag}
                {on && <X className="w-3 h-3" />}
              </button>
            );
          })}
          {selectedTags.length > 0 && (
            <button onClick={() => setSelectedTags([])} className="text-[11px] text-slate-500 hover:text-pink-600 ml-1 underline">clear</button>
          )}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center text-sm text-slate-400">
          {contacts.length === 0 ? 'no contacts yet — add one to get started' : 'no matches'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((c) => (
            <ContactCard key={c.id} contact={c} canEdit={isManager} isDuplicate={duplicateIds.has(c.id)} selectable={isManager} selected={selectedIds.has(c.id)} onToggleSelect={() => toggleSelect(c.id)} onClick={() => isManager && setEditing({ ...c })} onRemove={() => remove(c.id)} onSetTier={(t) => setTier(c.id, t)} onOpenMailer={(campaignId) => nav(`/mailers/${campaignId}`)} />
          ))}
        </div>
      )}

      {editing && (
        <ContactDialog
          draft={editing}
          onClose={() => setEditing(null)}
          onSave={save}
          reload={reload}
          allTags={allTags}
        />
      )}

      {isManager && selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-white border border-pink-200 shadow-2xl rounded-2xl px-3 py-2.5" data-testid="db-selection-bar">
          <span className="text-xs font-semibold text-pink-700 px-1">{selectedIds.size} selected</span>
          <div className="w-px h-6 bg-slate-200" />
          <Select value={addTarget} onValueChange={setAddTarget}>
            <SelectTrigger className="w-52 h-9 text-xs" data-testid="db-add-mailer-select"><SelectValue placeholder="choose a mailer…" /></SelectTrigger>
            <SelectContent>
              {campaigns.length === 0 ? (
                <div className="px-2 py-1.5 text-xs text-slate-400">no mailers yet</div>
              ) : campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={addSelectedToMailer} disabled={!addTarget || addingToMailer} className="h-9 text-xs bg-pink-500 hover:bg-pink-600 text-white" data-testid="db-add-to-mailer-btn">
            {addingToMailer ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
            add to mailer
          </Button>
          <button onClick={clearSelection} className="text-slate-400 hover:text-slate-700 p-1" title="clear selection" data-testid="db-clear-selection"><X className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
};

const DB_STATUS = {
  green:  { bg: '#e7f6ec', border: '#c3e9d1', ink: '#157a4e', avatar: '#cdeedd', dot: '#1f9d5b', label: 'posted' },
  pink:   { bg: '#fbe7ee', border: '#f5c6d8', ink: '#9d174d', avatar: '#f8d4e2', dot: '#db2777', label: 'product sent' },
  yellow: { bg: '#faf3d9', border: '#efe3ad', ink: '#866a16', avatar: '#f3e7b6', dot: '#d9a31a', label: 'to engage' },
};

const ContactCard = ({ contact, canEdit, isDuplicate, selectable, selected, onToggleSelect, onClick, onRemove, onSetTier, onOpenMailer }) => {
  const hist = contact.mailer_history || [];
  const hasPosted = Boolean(contact.content_posted) || hist.some((h) => h.content_posted);
  const hasSent = hist.length > 0;
  const statusKey = hasPosted ? 'green' : hasSent ? 'pink' : 'yellow';
  const pal = DB_STATUS[statusKey];
  return (
  <div
    className={`group relative border rounded-2xl p-4 transition cursor-pointer hover:shadow-md hover:-translate-y-0.5 ${isDuplicate ? 'border-red-300 bg-red-50/60 hover:border-red-400' : ''} ${selected ? 'ring-2 ring-pink-400' : ''}`}
    style={isDuplicate ? undefined : { background: pal.bg, borderColor: pal.border }}
    onClick={onClick}
    data-testid={`db-card-${contact.id}`}
  >
    {selectable && (
      <button
        onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
        className={`absolute top-3 right-3 w-5 h-5 rounded-md border flex items-center justify-center transition ${selected ? 'bg-pink-500 border-pink-500 text-white opacity-100' : 'bg-white/80 border-slate-300 text-transparent opacity-0 group-hover:opacity-100'}`}
        title={selected ? 'deselect' : 'select'}
        data-testid={`db-select-${contact.id}`}
      >
        <Check className="w-3.5 h-3.5" />
      </button>
    )}
    <div className="flex items-start gap-2">
      <span className="shrink-0 w-9 h-9 rounded-full overflow-hidden flex items-center justify-center" style={{ background: pal.avatar, border: `1px solid ${pal.border}` }} data-testid={`db-avatar-${contact.id}`}>
        {contact.photo_url ? (
          <img src={contact.photo_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <User className="w-4 h-4" style={{ color: pal.ink, opacity: 0.6 }} />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate flex items-center gap-1.5" style={{ color: isDuplicate ? undefined : pal.ink }}>
          <span className="truncate">{contact.name}</span>
          <TierBadge tier={contact.tier} editable={canEdit} onCycle={onSetTier} testId={`db-tier-badge-${contact.id}`} />
          {contact.custom_tag && (
            <span className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-semibold shrink-0 bg-lilac-100 text-lilac-700" data-testid={`db-custom-tag-${contact.id}`}>
              {contact.custom_tag}
            </span>
          )}
          {isDuplicate && (
            <span className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold shrink-0 uppercase tracking-wide text-red-700 bg-red-100 border border-red-300" data-testid={`db-duplicate-tag-${contact.id}`}>
              duplicate
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 truncate">{[contact.role, contact.company].filter(Boolean).join(' · ') || <span className="italic text-slate-400">no role</span>}</div>
      </div>
      {canEdit && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-600 shrink-0 mt-6" title="remove">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
    {(contact.mailer_history || []).some((h) => h.content_posted) && (
      <div className="mt-2 flex flex-wrap gap-1">
        {contact.mailer_history.filter((h) => h.content_posted).map((h) => (
          <button
            key={h.campaign_id}
            onClick={(e) => { e.stopPropagation(); onOpenMailer(h.campaign_id); }}
            className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
            title="open this mailer"
            data-testid={`db-mailer-posted-${contact.id}-${h.campaign_id}`}
          >
            posted: {h.campaign_name}
          </button>
        ))}
      </div>
    )}
    {(contact.email || contact.phone || contact.handle || contact.ig_link || contact.tt_link) && (
      <div className="mt-2 space-y-0.5 text-[11px] text-slate-600">
        {contact.email && <div className="flex items-center gap-1.5 truncate"><Mail className="w-3 h-3 text-slate-400" />{contact.email}</div>}
        {contact.phone && <div className="flex items-center gap-1.5 truncate"><Phone className="w-3 h-3 text-slate-400" />{contact.phone}</div>}
        {!contact.ig_link && !contact.tt_link && contact.handle && <div className="truncate text-pink-600">{contact.handle}</div>}
        {contact.ig_link && (
          <div className="truncate text-pink-600">IG{contact.ig_followers != null ? ` · ${Number(contact.ig_followers).toLocaleString()} followers` : ''}</div>
        )}
        {contact.tt_link && (
          <div className="truncate text-pink-600">TT{contact.tt_followers != null ? ` · ${Number(contact.tt_followers).toLocaleString()} followers` : ''}</div>
        )}
      </div>
    )}
    {(contact.tags || []).length > 0 && (
      <div className="mt-3 flex flex-wrap gap-1">
        {contact.tags.map((t) => (
          <span key={t} className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-semibold" style={{ background: '#fce7f3', color: '#9d174d', border: '1px solid #f9a8c8' }}>{t}</span>
        ))}
      </div>
    )}
    {!isDuplicate && (
      <div className="mt-3 flex items-center gap-1.5" data-testid={`db-status-${contact.id}`}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: pal.dot }} />
        <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: pal.ink, opacity: 0.85 }}>{pal.label}</span>
      </div>
    )}
  </div>
  );
};

const ContactDialog = ({ draft, onClose, onSave, reload, allTags }) => {
  const [form, setForm] = useState(draft);
  const [tagInput, setTagInput] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const mailerHistory = draft.mailer_history || [];
  const [mailerNotes, setMailerNotes] = useState(() =>
    Object.fromEntries(mailerHistory.map((h) => [h.campaign_id, h.notes || '']))
  );
  const origMailerNotes = useMemo(
    () => Object.fromEntries(mailerHistory.map((h) => [h.campaign_id, h.notes || ''])),
    [draft.id]
  );
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handlePhoto = async (file) => {
    setPhotoUploading(true);
    try { const url = await uploadImage(file); set({ photo_url: url }); }
    catch { toast.error('photo upload failed'); }
    finally { setPhotoUploading(false); }
  };

  const addTag = (val) => {
    const t = (val || tagInput).trim();
    if (!t) return;
    if (!(form.tags || []).includes(t)) set({ tags: [...(form.tags || []), t] });
    setTagInput('');
  };
  const removeTag = (t) => set({ tags: (form.tags || []).filter((x) => x !== t) });

  const canSave = (form.name || '').trim().length > 0;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const changed = Object.keys(mailerNotes).filter((cid) => (mailerNotes[cid] || '') !== (origMailerNotes[cid] || ''));
      for (const cid of changed) {
        await api.patch(`/database/contacts/${form.id}/mailer-notes`, { campaign_id: cid, notes: mailerNotes[cid] || '' });
      }
      await onSave(form);
      if (changed.length && reload) await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden border-pink-200 shadow-2xl">
        <div className="bg-gradient-to-br from-pink-100 via-pink-50 to-white px-4 py-3 border-b border-pink-200">
          <DialogTitle className="text-sm font-semibold text-pink-700">{draft.id ? 'edit contact' : 'new contact'}</DialogTitle>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <label className="shrink-0 w-14 h-14 rounded-full overflow-hidden border-2 border-pink-200 flex items-center justify-center cursor-pointer bg-pink-50 hover:border-pink-400 relative group" data-testid="db-dialog-photo">
              {form.photo_url ? (
                <img src={form.photo_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-pink-300" />
              )}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity">
                {photoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              </div>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handlePhoto(f); }} data-testid="db-dialog-photo-input" />
            </label>
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-widest text-pink-500 font-semibold">name</label>
              <div className="mt-1 bg-pink-50 border-2 border-pink-200 rounded-2xl px-3 py-1.5">
                <Input autoFocus value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="who is this?" className="h-7 px-0 text-sm font-semibold text-pink-800 bg-transparent border-none outline-none focus-visible:ring-0" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">company</label>
              <Input value={form.company || ''} onChange={(e) => set({ company: e.target.value })} className="mt-1 h-9 text-xs" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">role</label>
              <Input value={form.role || ''} onChange={(e) => set({ role: e.target.value })} className="mt-1 h-9 text-xs" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">email</label>
              <Input value={form.email || ''} onChange={(e) => set({ email: e.target.value })} className="mt-1 h-9 text-xs" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">phone</label>
              <Input value={form.phone || ''} onChange={(e) => set({ phone: e.target.value })} className="mt-1 h-9 text-xs" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">handle / IG</label>
            <Input value={form.handle || ''} onChange={(e) => set({ handle: e.target.value })} className="mt-1 h-9 text-xs" placeholder="@..." />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">custom tag</label>
            <Input value={form.custom_tag || ''} onChange={(e) => set({ custom_tag: e.target.value })} className="mt-1 h-9 text-xs" placeholder="e.g. VIP" data-testid="db-dialog-custom-tag" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">instagram link</label>
              <Input value={form.ig_link || ''} onChange={(e) => set({ ig_link: e.target.value })} className="mt-1 h-9 text-xs" placeholder="https://instagram.com/…" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">ig followers</label>
              <NumberInput value={form.ig_followers ?? ''} onChange={(v) => set({ ig_followers: v })} className="mt-1 h-9 text-xs" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">tiktok link</label>
              <Input value={form.tt_link || ''} onChange={(e) => set({ tt_link: e.target.value })} className="mt-1 h-9 text-xs" placeholder="https://tiktok.com/@…" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">tt followers</label>
              <NumberInput value={form.tt_followers ?? ''} onChange={(v) => set({ tt_followers: v })} className="mt-1 h-9 text-xs" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-pink-500 font-semibold">tier</label>
            <div className="mt-1"><TierPicker tier={form.tier} onPick={(t) => set({ tier: t })} testIdPrefix="db-dialog-tier" /></div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-pink-500 font-semibold">tags</label>
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {(form.tags || []).map((t) => (
                <span key={t} className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] font-semibold" style={{ background: '#fce7f3', color: '#9d174d', border: '1px solid #f9a8c8' }}>
                  {t}
                  <button type="button" onClick={() => removeTag(t)} className="opacity-60 hover:opacity-100">×</button>
                </span>
              ))}
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } if (e.key === 'Backspace' && !tagInput && (form.tags || []).length) removeTag(form.tags[form.tags.length - 1]); }}
                placeholder="add tag + enter"
                className="h-6 text-[11px] w-32 border-dashed"
              />
            </div>
            {allTags.length > 0 && (
              <div className="mt-1.5 text-[10px] text-slate-400">
                existing:{' '}
                {allTags.filter((t) => !(form.tags || []).includes(t)).map((t) => (
                  <button key={t} type="button" onClick={() => addTag(t)} className="underline text-slate-500 hover:text-pink-600 mr-1">{t}</button>
                ))}
              </div>
            )}
          </div>
          {mailerHistory.length > 0 && (
            <div data-testid="db-dialog-mailer-notes">
              <label className="text-[10px] uppercase tracking-widest text-pink-500 font-semibold">mailer notes</label>
              <p className="text-[10px] text-slate-400 mb-1.5">notes specific to each mailer this contact is part of</p>
              <div className="space-y-2">
                {mailerHistory.map((h) => (
                  <div key={h.campaign_id} className="bg-pink-50/60 border border-pink-100 rounded-xl p-2">
                    <div className="text-[11px] font-semibold text-pink-700 truncate mb-1">{h.campaign_name}</div>
                    <Textarea
                      value={mailerNotes[h.campaign_id] ?? ''}
                      onChange={(e) => setMailerNotes((m) => ({ ...m, [h.campaign_id]: e.target.value }))}
                      rows={2}
                      placeholder="notes for this mailer…"
                      className="text-xs bg-white"
                      data-testid={`db-dialog-mailer-note-${h.campaign_id}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">general notes</label>
            <Textarea value={form.notes || ''} onChange={(e) => set({ notes: e.target.value })} rows={2} className="mt-1 text-xs" placeholder="notes about this contact overall…" data-testid="db-dialog-general-notes" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">address</label>
            <Textarea value={form.address || ''} onChange={(e) => set({ address: e.target.value })} rows={2} className="mt-1 text-xs" data-testid="db-dialog-address" />
          </div>
        </div>
        <DialogFooter className="px-4 pb-4 pt-0">
          <Button variant="ghost" onClick={onClose} className="h-8 text-xs">cancel</Button>
          <Button onClick={handleSave} disabled={!canSave || saving} className="h-8 text-xs bg-pink-500 hover:bg-pink-600 text-white" data-testid="db-save">{saving ? 'saving…' : 'save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Database;
